import {
  PaginatedTicketBillableItems,
  TicketBillableItem,
  TicketBillableItemInput,
  TicketBillableItemUpdate
} from 'types/BillingTypes';
import useHttp from '../helpers/HttpMethods';

const http = useHttp();

class TicketBillableItemService {
  /**
   * Lista os itens faturáveis de um ticket paginados.
   * @param {number} ticketId - ID do ticket.
   * @param {string} filter - Filtros em string.
   * @returns {Promise<PaginatedTicketBillableItems | Response>} - Itens faturáveis em formato paginado.
   */
  static async listByTicket(
    ticketId: number,
    filter: string
  ): Promise<PaginatedTicketBillableItems | Response> {
    return await http.get(`/tickets/${ticketId}/billable-items?${filter}`);
  }

  /**
   * Busca um item faturável por ID.
   * @param {number} ticketId - ID do ticket.
   * @param {number} id - ID do item faturável.
   * @returns {Promise<TicketBillableItem | Response>} - Dados completos do item faturável.
   */
  static async findById(
    ticketId: number,
    id: number
  ): Promise<TicketBillableItem | Response> {
    return await http.get(`/tickets/${ticketId}/billable-items/${id}`);
  }

  /**
   * Cria um novo item faturável.
   * @param {number} ticketId - ID do ticket.
   * @param {TicketBillableItemInput} body - Dados do item faturável a ser criado.
   * @returns {Promise<TicketBillableItem | Response>} - Item faturável criado ou resposta de erro.
   */
  static async create(
    ticketId: number,
    body: TicketBillableItemInput
  ): Promise<TicketBillableItem | Response> {
    return await http.post(body, `/tickets/${ticketId}/billable-items`);
  }

  /**
   * Edita um item faturável existente.
   * @param {number} ticketId - ID do ticket.
   * @param {number} id - ID do item faturável a ser atualizado.
   * @param {TicketBillableItemUpdate} body - Dados do item faturável para atualização.
   * @returns {Promise<TicketBillableItem | Response>} - Item faturável atualizado ou resposta de erro.
   */
  static async edit(
    ticketId: number,
    id: number,
    body: TicketBillableItemUpdate
  ): Promise<TicketBillableItem | Response> {
    return await http.put(body, `/tickets/${ticketId}/billable-items/${id}`);
  }

  /**
   * Remove um item faturável.
   * @param {number} ticketId - ID do ticket.
   * @param {number} id - ID do item faturável.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async delete(ticketId: number, id: number): Promise<Response> {
    return await http.del(`/tickets/${ticketId}/billable-items/${id}`);
  }
}

export default TicketBillableItemService;

